<template>
  <v-container>
    <!-- Status and Permissions -->
    <v-row dense v-if="!isDelivery">
      <v-col cols="4">
        <v-switch
          v-model="employee.state"
          inset
          :label="$t('active')"
          :false-value="0"
          :true-value="1"
          dense
        />
      </v-col>
      <v-col cols="8" v-if="$route.params.parent_id || parentCompanies.length">
        <v-switch
          v-model="employee.is_transferable"
          inset
          :label="$t('change_location')"
          :false-value="0"
          :true-value="1"
          dense
        />
      </v-col>
      <v-col cols="4" v-if="employee.all_cooperation">
        <v-switch
          v-model="employee.can_change_restoran_company"
          inset
          :label="$t('can_change_restoran_company')"
          :false-value="0"
          :true-value="1"
          dense
        />
      </v-col>
      <v-col cols="12" v-if="employee.company?.no_time_limit">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-switch
                v-model="employee.no_time_limit_user"
                inset
                :label="$t('time_limit')"
                :false-value="0"
                :true-value="1"
                dense
                hide-details
              />
            </div>
          </template>
          <span>
            {{ $t("time_limit_tooltip_line1") }}<br />
            {{ $t("time_limit_tooltip_line2") }}
          </span>
        </v-tooltip>
      </v-col>
    </v-row>

    <!-- Company Selection -->
    <v-row dense v-if="!isDelivery">
      <v-col cols="12">
        <autocomplete
          v-if="employee.id"
          v-model="employee.company"
          :items="parentCompanies || []"
          :label="$t('current_company')"
          item-text="name"
          item-id="id"
          @change="$emit('open-change-company')"
          return-object
          persistent-hint
          :hide-details="false"
          :hint="$t('change_office_meals_note')"
        />
      </v-col>
    </v-row>

    <v-divider class="my-2" v-if="!isDelivery" />

    <!-- Basic Info -->
    <v-row dense>
      <v-col cols="6">
        <text-field
          v-model="employee.name"
          :rules="[rules.required]"
          :label="$t('name')"
          required
        />
      </v-col>
      <v-col cols="6">
        <text-field
          v-model="employee.lastname"
          :rules="[rules.required]"
          :label="$t('last_name')"
          required
        />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="6">
        <text-field
          v-model="employee.email"
          :rules="isDelivery && employee.id ? [] : [rules.required]"
          :label="$t('email_or_username')"
          :readonly="isDelivery && !!employee.id"
          :required="!(isDelivery && employee.id)"
        />
      </v-col>
      <v-col cols="6" v-if="!isDelivery">
        <text-field
          type="email"
          v-model="employee.private_email"
          :label="$t('private_email')"
        />
      </v-col>
      <v-col cols="6" v-if="isDelivery">
        <text-field v-model="employee.phone" :label="$t('phone')" dense />
      </v-col>
    </v-row>

    <!--
      Sve tri celine ispod (Lični podaci / Naplata i ugovor / Preporuke) se
      UVIJEK prikazuju kad je type "dostava" - i na kreiranju i na izmjeni
      (namjerno, 15.09 odluka) - dispečer treba da može popuniti sve odjednom
      dok kreira kurira, ne samo naknadno kroz izmjenu.

      POST .../users-delivery čuva ova polja ako stignu FLAT na vrhu tijela
      (15.09.2026 backend odgovor, N2) - vidi
      CompanyEmployeeDialog.createEmployee(), koji ih tako šalje i izbacuje
      nested pivot/detail. Ranije "ne čuva" je bio oblik tijela, ne bug.
      Raspored po celinama prati backend odgovor
      14.09.2026 - forma podeljena isto kao tri backend tabele (users /
      user_details / delivery_company_user), radi lakšeg snalaženja u UI-ju.

      contact_phone (pivot) namerno više NIJE prikazan - redundantan je sa
      user_details.emergency_contact_phone (isti "kontakt telefon"), backend
      ga zadržava u bazi radi kompatibilnosti ali preporučuje da nova forma
      koristi isključivo emergency_contact_* (14.09.2026 answer).

      Samo za kurire (type "dostava"), ne i za dispečere ("admin_delivery") -
      lični podaci/ugovor/preporuke su podaci o kuriru na terenu, dispečer ih
      nema.
    -->
    <template v-if="isDelivery && employee.type === 'dostava'">
      <v-row dense>
        <v-col cols="12" class="mb-0 pb-0">
          <p class="text-left font-weight-bold mb-1">{{ $t("personal_data_section") }}</p>
        </v-col>
        <v-col cols="6">
          <date-picker
            v-model="detailDateOfBirth"
            single-date
            :label="$t('date_of_birth')"
          />
        </v-col>
        <v-col cols="6">
          <text-field v-model="detailIban" label="IBAN" dense />
        </v-col>
        <v-col cols="6">
          <text-field
            v-model="detailEmergencyContactName"
            :label="$t('emergency_contact_name')"
            dense
          />
        </v-col>
        <v-col cols="6">
          <text-field
            v-model="detailEmergencyContactPhone"
            :label="$t('emergency_contact_phone')"
            dense
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12" class="mb-0 pb-0">
          <p class="text-left font-weight-bold mb-1">
            {{ $t("courier_payment_and_contract_section") }}
          </p>
        </v-col>
        <v-col cols="6">
          <text-field v-model="pivotBankAccount" :label="$t('bank_account')" dense />
        </v-col>
        <v-col cols="6">
          <autocomplete
            v-model="pivotPayingType"
            :items="courierPayingTypeOptions"
            item-text="name"
            item-value="val"
            :label="$t('courier_pay_type')"
            dense
          />
        </v-col>
        <v-col cols="6">
          <text-field
            v-model="pivotPaying"
            :label="$t('amount')"
            :suffix="courierPaymentSuffix"
            dense
          />
        </v-col>
        <v-col cols="6">
          <text-field v-model="pivotNote" :label="$t('note')" dense />
        </v-col>
        <v-col cols="6">
          <date-picker
            v-model="pivotContractSignedAt"
            single-date
            :label="$t('contract_signed_at')"
          />
        </v-col>
        <v-col cols="6">
          <date-picker
            v-model="pivotContractActiveFrom"
            single-date
            :label="$t('contract_active_from')"
            :hint="$t('contract_active_from_hint')"
            persistent-hint
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12" class="mb-0 pb-0">
          <p class="text-left font-weight-bold mb-1">{{ $t("referrals_section") }}</p>
        </v-col>
        <v-col cols="6">
          <text-field v-model="detailReferralUrl" :label="$t('referral_url')" dense />
        </v-col>
        <v-col cols="6">
          <text-field v-model="detailReferralShortUrl" :label="$t('referral_short_url')" dense />
        </v-col>
        <v-col cols="6">
          <text-field v-model="detailReferredBy" :label="$t('referred_by')" dense />
        </v-col>
      </v-row>
    </template>

    <!-- Company/Office and Password -->
    <v-row dense>
      <v-col
        cols="6"
        v-if="
          !isDelivery &&
          $store.state.company &&
          ($store.state.company.children || []).length > 0 &&
          employee.id
        "
      >
        <dropdown
          :items="$store.state.company.children"
          :label="$t('office')"
          item-text="name"
          item-value="id"
          :rules="[$t('required_field')]"
          v-model="employee.company_id"
          dense
        />
      </v-col>
      <v-col cols="6" v-if="!isDelivery || !employee.id || isAdmin">
        <text-field
          v-model="employee.password"
          :label="employee.id ? $t('enter_new_password') : $t('password')"
          type="password"
          prevent-prefill
          :required="!employee.id"
          :rules="employee.id ? [] : [rules.required, rules.password]"
        />
      </v-col>
      <v-col :cols="isDelivery && employee.id && !isAdmin ? 12 : 6">
        <employee-role-picker v-model="employee.type" :company="selectedCompany" />
      </v-col>
    </v-row>

    <!-- Contract switches for recipient -->
    <v-row dense v-if="employee.type === 'recipient'">
      <v-col cols="6">
        <v-switch
          v-model="employee.is_contract"
          inset
          :label="$t('is_contract')"
          :false-value="0"
          :true-value="1"
          dense
        />
      </v-col>
      <v-col cols="6">
        <v-switch
          v-model="employee.is_non_contract"
          inset
          :label="$t('is_non_contract')"
          :false-value="0"
          :true-value="1"
          dense
        />
      </v-col>
    </v-row>

    <!-- Offices for recipient -->
    <v-row dense v-if="employee.type == 'recipient' && companies[0]">
      <v-col cols="12">
        <autocomplete
          v-model="searchs"
          :items="companies[0].children.filter((i) => i.name)"
          dense
          filled
          multiple
          :label="$t('choose_office')"
          item-text="name"
          item-value="id"
          clear
          chips
          small-chips
        >
          <v-btn
            color="primary"
            slot="prepend-item"
            @click="$emit('update:searchs', [])"
            small
            class="mx-4"
          >
            {{ $t("display_all") }}
          </v-btn>
        </autocomplete>
      </v-col>
    </v-row>

    <v-divider class="my-2" v-if="!isDelivery" />

    <!-- Address, Phone, WorkPos, External Code, Team -->
    <v-row dense v-if="!isDelivery">
      <v-col cols="6">
        <text-field v-model="employee.address" :label="$t('address')" dense />
      </v-col>
      <v-col cols="6">
        <text-field v-model="employee.phone" :label="$t('phone')" dense />
      </v-col>
      <v-col cols="6">
        <text-field v-model="employee.work_position" :label="$t('work_place')" dense />
      </v-col>
      <v-col cols="6">
        <text-field v-model="employee.external_code" :label="$t('external_id')" dense />
      </v-col>
      <v-col cols="6">
        <text-field :label="$t('team')" v-model="employee.team" type="number" dense />
      </v-col>
      <v-col cols="6">
        <autocomplete
          :label="$t('language')"
          v-model="employee.lang"
          type="number"
          dense
          :items="availableLanguages"
          item-text="text"
          item-value="code"
        />
      </v-col>
    </v-row>

    <v-divider class="my-2" />

    <!-- Dates -->
    <v-row dense>
      <v-col cols="12" class="mb-0 pb-0">
        <p class="text-left font-weight-bold">{{ $t("account_creation_edit_date") }}:</p>
      </v-col>
      <v-col cols="6" :title="$t('registration_date')">
        <v-icon class="mr-2">mdi-calendar</v-icon>
        {{ moment(employee.created_at).format("DD.MM.YYYY. H:mm:ss") }}
      </v-col>
      <v-col cols="6" :title="$t('modification_date')">
        <v-icon class="mr-2">mdi-calendar-sync</v-icon>
        {{ moment(employee.updated_at).format("DD.MM.YYYY. H:mm:ss") }}
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from "moment";
import hasValidation from "@/mixins/hasValidation";
import EmployeeRolePicker from "@/components/company/fields/EmployeeRolePicker.vue";
import { getDeliveryCompanyCurrency } from "@/utils/currency";

export default {
  mixins: [hasValidation],
  name: "EmployeeFormSection",
  props: {
    employee: Object,
    parentCompanies: Array,
    companies: Array,
    errorMessageEmail: String,
    searchs: Array,
    selectedCompany: {
      type: Object,
      required: false,
    },
    // Delivery users: create (POST .../users-delivery) requires email + password,
    // so both show like a regular user there. Update (PATCH .../users-delivery/:id)
    // only documented to accept name / lastname / phone / type — email was
    // explicitly rejected by that endpoint (11_09_2026 backend answer, pt 4), so
    // on edit it's shown read-only. Whether that same PATCH accepts `password` is
    // unconfirmed with backend (see 09_09_2026 questions doc, still open) — it's
    // only offered to a platform admin (`isAdmin`) as an opt-in reset, sent only
    // when non-empty; verify against the API before relying on it. Every other
    // field stays hidden and, importantly, this avoids touching
    // $store.state.company.children — a delivery firm object (from /user/me
    // companies[]) has no `children`.
    isDelivery: {
      type: Boolean,
      default: false,
    },
    // The delivery firm itself, for its currency (courier payment suffix).
    deliveryCompany: {
      type: Object,
      required: false,
    },
  },
  components: {
    EmployeeRolePicker,
  },
  data() {
    return {
      moment,
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.user?.user?.type === "admin";
    },
    // delivery_company_user.paying_type - how the firm pays ITS OWN courier,
    // same 1/2/3 numbering as restaurant_delivery_company.paying_type
    // (confirmed 12.09.2026 backend answer, C1). Distinct from that relation
    // and from delivery_companies.paying_type ("platform_pay_type") - three
    // separate concepts that happen to share a column name.
    courierPayingTypeOptions() {
      return [
        { name: this.$t("by_month"), val: 1 },
        { name: this.$t("by_percent"), val: 2 },
        { name: this.$t("by_delivery"), val: 3 },
      ];
    },
    // "By percent" isn't a currency amount - only by_month/by_delivery are.
    // Same pattern as DeliveryCompanyDialog.platformPaymentSuffix /
    // RestaurantOwnDeliverySettingsDialog.paymentCurrency.
    courierPaymentSuffix() {
      if (Number(this.pivotPayingType) === 2) return "%";
      return getDeliveryCompanyCurrency(this.deliveryCompany) || "KM";
    },
    // These fields arrive nested under employee.pivot (per-firm pivot row on
    // delivery_company_user), same place `state` is read from for delivery
    // users (see src/pages/user/index.vue fetch mapping) - not flat on the
    // employee object. Proxied through computed get/set so the template can
    // bind them like any other field while $set keeps Vue 2 reactivity
    // working when `pivot` itself is missing.
    // contact_phone deliberately has no proxy anymore - see the template
    // comment above the "Lični podaci" section.
    pivotBankAccount: {
      get() {
        return this.employee.pivot?.bank_account ?? null;
      },
      set(value) {
        this.setPivotField("bank_account", value);
      },
    },
    pivotPayingType: {
      get() {
        return this.employee.pivot?.paying_type ?? null;
      },
      set(value) {
        this.setPivotField("paying_type", value);
      },
    },
    pivotPaying: {
      get() {
        return this.employee.pivot?.paying ?? null;
      },
      set(value) {
        this.setPivotField("paying", value);
      },
    },
    pivotNote: {
      get() {
        return this.employee.pivot?.note ?? null;
      },
      set(value) {
        this.setPivotField("note", value);
      },
    },
    // Datum potpisivanja ugovora SA OVOM firmom - može biti različit od
    // contract_active_from (potpisano danas, počinje kasnije). Novo polje,
    // 14.09.2026 backend answer.
    pivotContractSignedAt: {
      get() {
        return this.employee.pivot?.contract_signed_at ?? null;
      },
      set(value) {
        this.setPivotField("contract_signed_at", value);
      },
    },
    pivotContractActiveFrom: {
      get() {
        return this.employee.pivot?.contract_active_from ?? null;
      },
      set(value) {
        this.setPivotField("contract_active_from", value);
      },
    },
    // Lični podaci (user_details tabela, employee.detail u odgovoru) - vezani
    // za OSOBU, ne za firmu (ostaju isti ako kurir pređe u drugu firmu).
    // employee.detail je null dok korisnik nema nijedan od ovih podataka
    // unešen (nova tabela, trenutno prazna za sve postojeće korisnike) - zato
    // isti $set fallback obrazac kao za pivot.
    detailDateOfBirth: {
      get() {
        return this.employee.detail?.date_of_birth ?? null;
      },
      set(value) {
        this.setDetailField("date_of_birth", value);
      },
    },
    detailIban: {
      get() {
        return this.employee.detail?.iban ?? null;
      },
      set(value) {
        this.setDetailField("iban", value);
      },
    },
    detailEmergencyContactName: {
      get() {
        return this.employee.detail?.emergency_contact_name ?? null;
      },
      set(value) {
        this.setDetailField("emergency_contact_name", value);
      },
    },
    detailEmergencyContactPhone: {
      get() {
        return this.employee.detail?.emergency_contact_phone ?? null;
      },
      set(value) {
        this.setDetailField("emergency_contact_phone", value);
      },
    },
    detailReferralUrl: {
      get() {
        return this.employee.detail?.referral_url ?? null;
      },
      set(value) {
        this.setDetailField("referral_url", value);
      },
    },
    detailReferralShortUrl: {
      get() {
        return this.employee.detail?.referral_short_url ?? null;
      },
      set(value) {
        this.setDetailField("referral_short_url", value);
      },
    },
    detailReferredBy: {
      get() {
        return this.employee.detail?.referred_by ?? null;
      },
      set(value) {
        this.setDetailField("referred_by", value);
      },
    },
    availableLanguages() {
      const restaurant =
        this.$store.state.restaurant || this.$store.state.company?.restaurant;
      const restaurantLanguages = restaurant?.settings?.language || [];
      if (restaurantLanguages.length === 0) {
        return this.getAvailableLanguages();
      }
      return this.getAvailableLanguages().filter((lang) =>
        restaurantLanguages.includes(lang.code)
      );
    },
  },
  methods: {
    setPivotField(field, value) {
      if (!this.employee.pivot) {
        this.$set(this.employee, "pivot", {});
      }
      this.$set(this.employee.pivot, field, value);
    },
    setDetailField(field, value) {
      if (!this.employee.detail) {
        this.$set(this.employee, "detail", {});
      }
      this.$set(this.employee.detail, field, value);
    },
  },
  emits: ["open-change-company", "update:searchs"],
};
</script>

<style>
/* Hide scrollbar for the dialog card content */
.ad::-webkit-scrollbar {
  display: none;
}
.ad .v-container::-webkit-scrollbar {
  display: none;
}
</style>
